import { HighlightsAnalytics } from '../HighlightsAnalytics';
import { SdkLoader } from './SdkLoader';
import { StateManager } from './StateManager';

import type {
  AdPlayerStateHook,
  AnalyticsContext,
  EventHandlers,
  HighlightsPlayerState,
  InitOptions,
  MiniPlayerActions,
  MuteChangeParamsType,
  OnExpandViewChangedPayloadType,
  PlayerStateHook,
  PlayPauseChangeParamsType,
  ReduxDispatch,
  VideoNotFoundParamsType,
} from './types';

/**
 * Singleton coordinator for managing Mini Player and Highlights player interactions.
 *
 * The PlayerCoordinator ensures that audio (iHeartRadio Mini Player) and video (Highlights)
 * playback are properly synchronized to prevent both playing simultaneously. Key behaviors:
 *
 * ## Core Coordination Rules:
 * 1. **Mini Player playing + Highlights muted** → No change (both can coexist)
 * 2. **Mini Player playing + Highlights unmuted + Highlights playing** → Pause Mini Player to prevent overlap
 * 3. **Highlights player pause** → Revert Mini Player state (if paused for Highlights, resume; if was paused, stay paused)
 * 4. **Highlights player mute** → Revert Mini Player state (if paused for Highlights, resume; if was paused, stay paused)
 * 5. **Mini Player play when Highlights paused+unmuted** → No Highlights mute change (preserve pause state)
 * 6. **Mini Player playing + Highlights unmuted + Highlights paused** → No change (no audio overlap since Highlights is paused)
 *
 * ## Special Rules:
 * - Unmuting a Highlights player only pauses Mini Player if Highlights player is actually playing
 * - Clicking play on a muted Highlights player does NOT pause current Mini Player
 * - Mute state persistence carries over between different contexts
 * - Page refresh resets coordination state to defaults
 *
 * This creates a seamless user experience where only one media source plays audio at a time.
 */
class PlayerCoordinator {
  private static instance: PlayerCoordinator;

  private stateManager: StateManager;

  private sdkLoader: SdkLoader;

  private eventHandlers: EventHandlers;

  private onVideoNotFoundCallback?: () => void;

  private onFeedLoadedCallback?: () => void;

  /** Analytics instance for tracking highlights events */
  private analytics: HighlightsAnalytics;

  /** Context for analytics (stationId, podcastId, etc.) */
  private analyticsContext?: AnalyticsContext;

  private constructor() {
    this.stateManager = new StateManager();
    this.sdkLoader = new SdkLoader();
    this.eventHandlers = {};
    this.analytics = new HighlightsAnalytics();
  }

  /** Returns the singleton instance of PlayerCoordinator */
  public static getInstance(): PlayerCoordinator {
    if (!PlayerCoordinator.instance) {
      PlayerCoordinator.instance = new PlayerCoordinator();
    }
    return PlayerCoordinator.instance;
  }

  /** Returns a readonly copy of current state for external access */
  public getState(): Readonly<HighlightsPlayerState> {
    return this.stateManager.getState();
  }

  /** Sets the player actions from usePlayerActions hook */
  public setPlayerActions(playerActions: MiniPlayerActions): void {
    this.stateManager.setPlayerActions(playerActions);
  }

  /** Sets the Redux dispatch function for opening modals */
  public setDispatch(dispatch: ReduxDispatch): void {
    this.sdkLoader.setDispatch(dispatch);
  }

  /** Sets the player state from usePlayerState hook */
  public setPlayerState(
    playerStateHook: PlayerStateHook,
    adPlayerState: AdPlayerStateHook,
  ): void {
    this.stateManager.setPlayerState(playerStateHook, adPlayerState);
  }

  /** Handles Highlights player play event - implements coordination rules */
  private handleVideoPlay = (payload: PlayPauseChangeParamsType): void => {
    this.stateManager.set('isHighlightsPlayerPlaying', true);

    if (
      !payload.payload.muted &&
      this.stateManager.get('isMiniPlayerPlaying')
    ) {
      this.stateManager.set('miniPlayerShouldPlayAgain', true);
      this.stateManager.set('miniPlayerPausedByCoordination', true);
      this.stateManager.pauseMiniPlayer();
    }
  };

  /**
   * Handles Highlights player pause event - implements coordination rules.
   */
  private handleVideoPause = (payload: PlayPauseChangeParamsType): void => {
    this.stateManager.set('isHighlightsPlayerPlaying', false);

    if (
      !payload.payload.muted &&
      this.stateManager.get('miniPlayerShouldPlayAgain')
    ) {
      this.stateManager.set('miniPlayerShouldPlayAgain', false);

      if (!this.stateManager.get('expandViewOpen')) {
        this.stateManager.set('miniPlayerPausedByCoordination', false);
      }

      this.stateManager.set('bypassMuteCall', true);
      this.stateManager.playMiniPlayer();
    }
  };

  /**
   * Handles expand view state changes from the Highlights player.
   */
  private onExpandViewChanged = (
    payload: OnExpandViewChangedPayloadType,
  ): void => {
    const isOpening = payload.payload;

    if (isOpening) {
      this.stateManager.set(
        'miniPlayerWasPlayingBeforeExpand',
        this.stateManager.get('isMiniPlayerPlaying'),
      );

      if (
        this.stateManager.get('isHighlightsPlayerMuted') &&
        this.stateManager.get('isMiniPlayerPlaying')
      ) {
        this.stateManager.pauseMiniPlayer();
      }
    } else {
      this.stateManager.set('expandViewOpen', false);

      const highlightsNotPlayingAudio =
        this.stateManager.get('isHighlightsPlayerMuted') ||
        !this.stateManager.get('isHighlightsPlayerPlaying');

      const shouldResumeMiniPlayer =
        highlightsNotPlayingAudio &&
        (this.stateManager.get('miniPlayerWasPlayingBeforeExpand') ||
          this.stateManager.get('miniPlayerPausedByCoordination')) &&
        !this.stateManager.get('isMiniPlayerPlaying');

      if (shouldResumeMiniPlayer) {
        this.stateManager.playMiniPlayer();
        this.stateManager.set('miniPlayerPausedByCoordination', false);
      }

      this.stateManager.set('miniPlayerWasPlayingBeforeExpand', false);
      return;
    }

    this.stateManager.set('expandViewOpen', isOpening);
  };

  /**
   * Handles video not found events from Highlights SDK.
   */
  private handleVideoNotFound = (_payload: VideoNotFoundParamsType): void => {
    if (this.onVideoNotFoundCallback) {
      this.onVideoNotFoundCallback();
    }
  };

  /**
   * Handles Highlights player mute state changes and coordinates with Mini Player.
   */
  private handleVideoMuteChange = (
    payloadParam: MuteChangeParamsType,
  ): void => {
    const payload = payloadParam?.payload;

    if (typeof payload.muted === 'boolean') {
      this.stateManager.set('isHighlightsPlayerMuted', payload.muted);
      this.stateManager.set('lastHighlightsMuteState', payload.muted);

      if (this.stateManager.get('ignoreMuteChangeEvent')) {
        this.stateManager.set('ignoreMuteChangeEvent', false);
        return;
      }

      if (payload.muted && this.stateManager.get('miniPlayerShouldPlayAgain')) {
        this.stateManager.set('miniPlayerShouldPlayAgain', false);

        if (!this.stateManager.get('expandViewOpen')) {
          this.stateManager.set('miniPlayerPausedByCoordination', false);
        }

        this.stateManager.playMiniPlayer();
      } else if (
        !payload.muted &&
        this.stateManager.get('isHighlightsPlayerPlaying') &&
        this.stateManager.get('isMiniPlayerPlaying')
      ) {
        this.stateManager.set('miniPlayerShouldPlayAgain', true);
        this.stateManager.set('miniPlayerPausedByCoordination', true);
        this.stateManager.pauseMiniPlayer();
      }
    }
  };

  /**
   * Handles feed loaded event from Highlights SDK.
   */
  private handleFeedLoaded = (): void => {
    if (this.onFeedLoadedCallback) {
      this.onFeedLoadedCallback();
    }
  };

  /**
   * Registers event listeners for Highlights video player events.
   */
  private attachEventListeners(): void {
    if (window.genuin) {
      this.eventHandlers.onPlay = this.handleVideoPlay;
      this.eventHandlers.onPause = this.handleVideoPause;
      this.eventHandlers.onMuteChange = this.handleVideoMuteChange;
      this.eventHandlers.onVideoNotFound = this.handleVideoNotFound;
      this.eventHandlers.onExpandViewChanged = this.onExpandViewChanged;
      this.eventHandlers.onFeedLoaded = this.handleFeedLoaded;

      window.genuin.on<PlayPauseChangeParamsType>(
        'onPlay',
        this.eventHandlers.onPlay,
      );
      window.genuin.on<PlayPauseChangeParamsType>(
        'onPause',
        this.eventHandlers.onPause,
      );
      window.genuin.on<MuteChangeParamsType>(
        'onMuteChange',
        this.eventHandlers.onMuteChange,
      );
      window.genuin.on<VideoNotFoundParamsType>(
        'onVideoNotFound',
        this.eventHandlers.onVideoNotFound,
      );
      window.genuin.on<OnExpandViewChangedPayloadType>(
        'onExpandViewChanged',
        this.eventHandlers.onExpandViewChanged,
      );
      window.genuin.on('onFeedLoaded', this.eventHandlers.onFeedLoaded);
    }
  }

  /**
   * Removes all Highlights event listeners and clears handler references.
   */
  private detachHighlightsEventListeners(): void {
    if (window.genuin && this.eventHandlers) {
      if (this.eventHandlers.onPlay) {
        window.genuin.off<PlayPauseChangeParamsType>(
          'onPlay',
          this.eventHandlers.onPlay,
        );
      }
      if (this.eventHandlers.onPause) {
        window.genuin.off<PlayPauseChangeParamsType>(
          'onPause',
          this.eventHandlers.onPause,
        );
      }
      if (this.eventHandlers.onMuteChange) {
        window.genuin.off<MuteChangeParamsType>(
          'onMuteChange',
          this.eventHandlers.onMuteChange,
        );
      }
      if (this.eventHandlers.onVideoNotFound) {
        window.genuin.off<VideoNotFoundParamsType>(
          'onVideoNotFound',
          this.eventHandlers.onVideoNotFound,
        );
      }
      if (this.eventHandlers.onExpandViewChanged) {
        window.genuin.off<OnExpandViewChangedPayloadType>(
          'onExpandViewChanged',
          this.eventHandlers.onExpandViewChanged,
        );
      }
      if (this.eventHandlers.onFeedLoaded) {
        window.genuin.off('onFeedLoaded', this.eventHandlers.onFeedLoaded);
      }
      this.eventHandlers = {};
    }
  }

  /**
   * Sets a callback to be invoked when video not found event occurs.
   */
  public setOnVideoNotFoundCallback(callback: () => void): void {
    this.onVideoNotFoundCallback = callback;
  }

  /**
   * Sets a callback to be invoked when the highlights feed is loaded.
   */
  public setOnFeedLoadedCallback(callback: () => void): void {
    this.onFeedLoadedCallback = callback;
  }

  /**
   * Checks if the player coordination is properly set up with both hooks
   */
  public isCoordinationReady(): boolean {
    const state = this.stateManager.getState();
    return !!(state.playerActions && state.playerStateHook);
  }

  /**
   * Gets current player coordination status for debugging
   */
  public getCoordinationStatus() {
    const state = this.stateManager.getState();
    const audioState = state.playerStateHook?.playbackState || 'UNKNOWN';
    const isAudioPlaying = audioState === 'PLAYING';

    return {
      isReady: this.isCoordinationReady(),
      audioState,
      audioActuallyPlaying: isAudioPlaying,
      videoMuted: state.isHighlightsPlayerMuted,
      videoPlaying: state.isHighlightsPlayerPlaying,
      miniPlayerPlaying: state.isMiniPlayerPlaying,
      coordinationActive: isAudioPlaying && state.isHighlightsPlayerMuted,
      flags: {
        miniPlayerShouldPlayAgain: state.miniPlayerShouldPlayAgain,
        highlightsPlayerShouldUnmuteAgain:
          state.highlightsPlayerShouldUnmuteAgain,
        ignoreMuteChangeEvent: state.ignoreMuteChangeEvent,
        bypassMuteCall: state.bypassMuteCall,
        miniPlayerWasPlayingBeforeExpand:
          state.miniPlayerWasPlayingBeforeExpand,
        miniPlayerPausedByCoordination: state.miniPlayerPausedByCoordination,
      },
      stationType: state.playerStateHook?.stationType,
      isPodcast: state.isPodcast,
      isInitialized: state.isInitialized,
      lastStateTransition: {
        from: audioState,
        coordinationTriggered:
          state.highlightsPlayerShouldUnmuteAgain ||
          state.miniPlayerShouldPlayAgain,
      },
      muteStatePersistence: {
        lastHighlightsMuteState: state.lastHighlightsMuteState,
        currentHighlightsMuteState: state.isHighlightsPlayerMuted,
        muteStateWillPersist:
          state.lastHighlightsMuteState === state.isHighlightsPlayerMuted,
      },
      playbackContext: {
        isManualHighlightsPlayback: state.isManualHighlightsPlayback,
        coordinationMode:
          state.isManualHighlightsPlayback ? 'manual_highlights' : (
            'auto_coordination'
          ),
      },
      expandViewState: {
        isOpen: state.expandViewOpen,
        miniPlayerWasPlayingBeforeExpand:
          state.miniPlayerWasPlayingBeforeExpand,
        shouldControlMiniPlayer:
          this.stateManager.shouldPerformActionOnMiniPlayer(),
        miniPlayerPausedByCoordination: state.miniPlayerPausedByCoordination,
      },
    };
  }

  /**
   * Single entry point to initialize the entire Highlights SDK integration.
   */
  public init(options?: InitOptions, isPodcast: boolean = false): void {
    const defaultScriptUrl = `https://media.begenuin.com/sdk/2.0.2/gen_sdk.min.js`;
    const scriptUrl = options?.scriptUrl || defaultScriptUrl;

    // Store analytics context
    this.analyticsContext = {
      stationId: options?.stationId,
      stationName: options?.stationName,
      podcastId: options?.podcastId,
      podcastName: options?.podcastName,
      sessionId: options?.sessionId,
    };

    this.stateManager.set('isPodcast', isPodcast);

    this.sdkLoader.setupAuthenticationCallback();
    this.sdkLoader.setupDNSOptimization();

    this.sdkLoader.loadScript(scriptUrl, () => {
      this.sdkLoader.initialize(options?.config, () => {
        this.attachEventListeners();
        this.stateManager.set('isInitialized', true);
      });

      // Initialize analytics
      this.analytics.initialize(this.analyticsContext);
    });
  }

  /**
   * Sets up coordination for audio player events.
   */
  public setupAudioPlayerSubscription(): (() => void) | undefined {
    const state = this.stateManager.getState();
    if (state.playerActions && state.playerStateHook) {
      this.performInitialCoordination();

      return () => {
        this.stateManager.set('miniPlayerShouldPlayAgain', false);
        this.stateManager.set('highlightsPlayerShouldUnmuteAgain', false);
      };
    }

    return undefined;
  }

  /**
   * Performs initial coordination setup based on current player states
   */
  private performInitialCoordination(): void {
    const state = this.stateManager.getState();
    if (!state.playerStateHook) return;

    const audioIsPlaying = state.playerStateHook.playbackState === 'PLAYING';
    const highlightsIsUnmuted = !state.isHighlightsPlayerMuted;
    const highlightsIsPlaying = state.isHighlightsPlayerPlaying;

    if (audioIsPlaying && highlightsIsUnmuted && highlightsIsPlaying) {
      this.stateManager.set('highlightsPlayerShouldUnmuteAgain', true);
      this.stateManager.muteHighlightsPlayer();
    }
  }

  /**
   * Handles highlights player unmute action.
   */
  public handleHighlightsUnmute(): void {
    this.stateManager.unmuteHighlightsPlayer();

    if (this.stateManager.get('isMiniPlayerPlaying')) {
      this.stateManager.set('miniPlayerShouldPlayAgain', true);
      this.stateManager.pauseMiniPlayer();
    }
  }

  /**
   * Gets the preserved mute state for restoration
   */
  public getPreservedMuteState(): boolean {
    return this.stateManager.get('lastHighlightsMuteState');
  }

  /**
   * Sets the preserved mute state
   */
  public setPreservedMuteState(muted: boolean): void {
    this.stateManager.set('lastHighlightsMuteState', muted);
  }

  /**
   * Resets coordinator to initial state and removes all event listeners.
   */
  public cleanup(): void {
    this.detachHighlightsEventListeners();
    this.analytics.destroy();
    this.sdkLoader.cleanup();
    this.sdkLoader.reset();
    this.stateManager.reset();
    this.eventHandlers = {};
    window.genuin?.destroy();
  }
}

// Export singleton instance for use across the application
export const playerCoordinator = PlayerCoordinator.getInstance();
